/**
 * Centralized path management utility for Manage Programs routes
 *
 * This module provides a single source of truth for all navigation paths
 * within the Manage Programs feature to prevent inconsistencies and typos.
 *
 * Usage Examples:
 * ```typescript
 * import {
 *   MANAGE_PROGRAMS_PATHS,
 *   buildProductLinePath,
 *   buildRpnPath,
 *   buildFpnPath,
 *   buildRpnUrl,
 *   buildFpnUrl
 * } from './utils/manageProgramsPathUtils';
 *
 * // Navigate to product line
 * navigate(buildProductLinePath('ABC123'));
 *
 * // Navigate to RPN
 * navigate(buildRpnPath('ABC123', 456));
 *
 * // Navigate to FPN
 * navigate(buildFpnPath('ABC123', 456, 789));
 *
 * // Generate full URL for external links
 * const rpnUrl = buildRpnUrl('ABC123', 456);
 * // Result: https://verity.device.finance.amazon.dev/manage-programs/product-code/ABC123/rpn/R456
 *
 * // Generate URL with section hash
 * const sectionUrl = buildRpnSectionUrl('ABC123', 456, 'status');
 * // Result: https://verity.device.finance.amazon.dev/manage-programs/product-code/ABC123/rpn/R456#status
 *
 * // Use in breadcrumbs
 * const breadcrumbs = [
 *   { text: 'Product Code ABC123', href: buildProductLinePath('ABC123') }
 * ];
 * ```
 */

import { getApplicationUrl } from 'src/utils/environmentUtils';

/**
 * Base path constants for Manage Programs
 */
export const MANAGE_PROGRAMS_PATHS = {
  /** Base path for Manage Programs feature */
  BASE: '/manage-programs',

  /** Relative path segments (used for route definitions) */
  RELATIVE: {
    PRODUCT_LINE: 'product-code/:productCode',
    RPN: 'product-code/:productCode/rpn/:rpnId',
    FPN: 'product-code/:productCode/rpn/:rpnId/fpn/:fpnId',
  },
} as const;

/**
 * Builds the absolute path for a Product Line detail page
 * @param productCode - The product code (e.g., 'ABC123')
 * @returns Absolute path to the product line page
 * @example buildProductLinePath('ABC123') // Returns: '/manage-programs/product-code/ABC123'
 */
export const buildProductLinePath = (productCode: string): string => {
  return `${MANAGE_PROGRAMS_PATHS.BASE}/product-code/${productCode}`;
};

/**
 * Builds the absolute path for an RPN detail page
 * @param productCode - The product code (e.g., 'ABC123')
 * @param rpnId - The RPN ID (numeric, will be prefixed with 'R')
 * @returns Absolute path to the RPN page
 * @example buildRpnPath('ABC123', 456) // Returns: '/manage-programs/product-code/ABC123/rpn/R456'
 */
export const buildRpnPath = (productCode: string, rpnDisplayId: string): string => {
  return `${MANAGE_PROGRAMS_PATHS.BASE}/product-code/${productCode}/rpn/${rpnDisplayId}`;
};

/**
 * Builds the absolute path for an FPN detail page
 * @param productCode - The product code (e.g., 'ABC123')
 * @param rpnId - The RPN ID (numeric, will be prefixed with 'R')
 * @param fpnId - The FPN ID (numeric, will be formatted as 'F' + 6 digits)
 * @returns Absolute path to the FPN page
 * @example buildFpnPath('ABC123', 456, 10652) // Returns: '/manage-programs/product-code/ABC123/rpn/R456/fpn/F010652'
 */
export const buildFpnPath = (productCode: string, rpnDisplayId: string, fpnDisplayId: string): string => {
  return `${MANAGE_PROGRAMS_PATHS.BASE}/product-code/${productCode}/rpn/${rpnDisplayId}/fpn/${fpnDisplayId}`;
};

/**
 * Type guard to check if a value is a valid path parameter
 * @param value - Value to check
 * @returns True if the value can be used as a path parameter
 */
export const isValidPathParam = (value: unknown): value is string => {
  return typeof value === 'string' || typeof value === 'number';
};

// =============================================================================
// Breadcrumb Text Formatting Utilities
// =============================================================================

/**
 * Formats the breadcrumb display text for a Product Line
 * @param productCode - The product code (e.g., 'ABC123')
 * @returns Formatted breadcrumb text
 * @example formatProductLineBreadcrumb('ABC123') // Returns: 'Product Code ABC123'
 */
export const formatProductLineBreadcrumb = (
  productCode: string | undefined | null,
  productLineName: string
): string => {
  return `${productCode} - ${productLineName}`;
};

export const formatRpnBreadcrumb = (rpnDisplayId: string | undefined | null, rpnName: string): string => {
  return `${rpnDisplayId} - ${rpnName}`;
};

export const formatFpnBreadcrumb = (fpnDisplayId: string | undefined | null, fpnName: string): string => {
  return `${fpnDisplayId} - ${fpnName}`;
};

// =============================================================================
// Full URL Building Utilities (with base URL)
// =============================================================================

/**
 * Gets the base URL for full URL generation
 * Uses the application URL from environment configuration
 * @returns Clean base URL without trailing slash
 */
const getBaseUrl = (): string => {
  let baseUrl = getApplicationUrl();

  // Remove trailing slash if present
  if (baseUrl.endsWith('/')) {
    baseUrl = baseUrl.slice(0, -1);
  }

  return baseUrl;
};

/**
 * Builds the full URL for a Product Line detail page
 * @param productCode - The product code (e.g., 'ABC123')
 * @returns Full URL to the product line page
 * @example buildProductLineUrl('ABC123')
 * // Returns: 'https://verity.device.finance.amazon.dev/manage-programs/product-code/ABC123'
 */
export const buildProductLineUrl = (productCode: string): string => {
  return `${getBaseUrl()}${buildProductLinePath(productCode)}`;
};

/**
 * Builds the full URL for an RPN detail page
 * @param productCode - The product code (e.g., 'ABC123')
 * @param rpnId - The RPN ID (numeric, will be prefixed with 'R')
 * @returns Full URL to the RPN page
 * @example buildRpnUrl('ABC123', 456)
 * // Returns: 'https://verity.device.finance.amazon.dev/manage-programs/product-code/ABC123/rpn/R456'
 */
export const buildRpnUrl = (productCode: string, rpnDisplayId: string): string => {
  return `${getBaseUrl()}${buildRpnPath(productCode, rpnDisplayId)}`;
};

/**
 * Builds the full URL for an FPN detail page
 * @param productCode - The product code (e.g., 'ABC123')
 * @param rpnId - The RPN ID (numeric, will be prefixed with 'R')
 * @param fpnId - The FPN ID (numeric, will be formatted as 'F' + 6 digits)
 * @returns Full URL to the FPN page
 * @example buildFpnUrl('ABC123', 456, 10652)
 * // Returns: 'https://verity.device.finance.amazon.dev/manage-programs/product-code/ABC123/rpn/R456/fpn/F010652'
 */
export const buildFpnUrl = (productCode: string, rpnDisplayId: string, fpnDisplayId: string): string => {
  return `${getBaseUrl()}${buildFpnPath(productCode, rpnDisplayId, fpnDisplayId)}`;
};

/**
 * Builds the full URL for an RPN detail page with a specific section hash
 * @param productCode - The product code (e.g., 'ABC123')
 * @param rpnId - The RPN ID (numeric, will be prefixed with 'R')
 * @param sectionId - Section ID to navigate to (e.g., 'status', 'street-dates')
 * @returns Full URL to the RPN page with section hash
 * @example buildRpnSectionUrl('ABC123', 456, 'status')
 * // Returns: 'https://verity.device.finance.amazon.dev/manage-programs/product-code/ABC123/rpn/R456#status'
 */
export const buildRpnSectionUrl = (productCode: string, rpnDisplayId: string, sectionId: string): string => {
  return `${buildRpnUrl(productCode, rpnDisplayId)}#${sectionId}`;
};

/**
 * Builds the full URL for an FPN detail page with a specific section hash
 * @param productCode - The product code (e.g., 'ABC123')
 * @param rpnId - The RPN ID (numeric, will be prefixed with 'R')
 * @param fpnId - The FPN ID (numeric, will be formatted as 'F' + 6 digits)
 * @param sectionId - Section ID to navigate to (e.g., 'financial-info')
 * @returns Full URL to the FPN page with section hash
 * @example buildFpnSectionUrl('ABC123', 456, 10652, 'financial-info')
 * // Returns: 'https://verity.device.finance.amazon.dev/manage-programs/product-code/ABC123/rpn/R456/fpn/F010652#financial-info'
 */
export const buildFpnSectionUrl = (
  productCode: string,
  rpnDisplayId: string,
  fpnDisplayId: string,
  sectionId: string
): string => {
  return `${buildFpnUrl(productCode, rpnDisplayId, fpnDisplayId)}#${sectionId}`;
};

// =============================================================================
// Path Building Utilities with Section Hash (for internal navigation)
// =============================================================================

/**
 * Builds the path for an RPN detail page with a specific section hash
 * @param productCode - The product code (e.g., 'ABC123')
 * @param rpnId - The RPN ID (numeric, will be prefixed with 'R')
 * @param sectionId - Section ID to navigate to (e.g., 'status', 'street-dates')
 * @returns Path to the RPN page with section hash
 * @example buildRpnSectionPath('ABC123', 456, 'status')
 * // Returns: '/manage-programs/product-code/ABC123/rpn/R456#status'
 */
export const buildRpnSectionPath = (productCode: string, rpnDisplayId: string, sectionId: string): string => {
  return `${buildRpnPath(productCode, rpnDisplayId)}#${sectionId}`;
};

/**
 * Builds the path for an FPN detail page with a specific section hash
 * @param productCode - The product code (e.g., 'ABC123')
 * @param rpnId - The RPN ID (numeric, will be prefixed with 'R')
 * @param fpnId - The FPN ID (numeric, will be formatted as 'F' + 6 digits)
 * @param sectionId - Section ID to navigate to (e.g., 'financial-info')
 * @returns Path to the FPN page with section hash
 * @example buildFpnSectionPath('ABC123', 456, 10652, 'financial-info')
 * // Returns: '/manage-programs/product-code/ABC123/rpn/R456/fpn/F010652#financial-info'
 */
export const buildFpnSectionPath = (
  productCode: string,
  rpnDisplayId: string,
  fpnDisplayId: string,
  sectionId: string
): string => {
  return `${buildFpnPath(productCode, rpnDisplayId, fpnDisplayId)}#${sectionId}`;
};
